import React from "react";
import {
  Maybe,
  OrderPayBreakDownWithAllocation,
} from "@api/graphql/generated/generated-types";
import { formatDescription } from "../../utils/formatHelpers";
import { useCourierLocale } from "@src/common/courierLocale";

interface DriverAllocationListProps {
  allocations: OrderPayBreakDownWithAllocation[] | undefined;
}

export function DriverAllocationList({
  allocations,
}: DriverAllocationListProps) {
  if (!allocations || allocations.length === 0) return null;

  return (
    <div className="mb-2">
      {allocations.map((allocation, index) => (
        <div className={index + 1 < allocations.length ? "border-b dark:border-gray-500 pb-2 mb-2" : ""} key={allocation.driverId}>
          <DriverAllocationItem allocation={allocation} />
        </div>
      ))}
    </div>
  );
}

interface DriverAllocationItemProps {
  allocation: OrderPayBreakDownWithAllocation;
}

function DriverAllocationItem({ allocation }: DriverAllocationItemProps) {
  const { currencyDisplay } = useCourierLocale();
  const allocationValue =
    allocation.allocationAmount !== null
      ? `${currencyDisplay}${parseFloat(allocation.allocationAmount).toFixed(2)}`
      : `${(parseFloat(allocation.allocationRate || 1) * 100).toFixed(0)}%`;

  const { driver, breakdown } = allocation;

  return (
    <div className="border-gray-200 last:border-0">
      <div className="flex justify-between items-center mb-1">
        <span className="font-medium dark:text-gray-200">
          {driver?.firstName} {driver?.lastName}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Allocation: {allocationValue}
        </span>
      </div>

      <div className="pl-2">
        {breakdown.items.map((item, idx) => (
          <BreakdownItem
            key={idx}
            name={item.name}
            description={item.description}
            amount={item.amount}
          />
        ))}

        <div className="flex justify-between mt-1 font-medium text-sm dark:text-gray-200">
          <span>Total</span>
          <span>{currencyDisplay}{parseFloat(breakdown.total).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

interface BreakdownItemProps {
  name: string;
  description: Maybe<string> | undefined;
  amount: string;
}

function BreakdownItem({ name, description, amount }: BreakdownItemProps) {
  const { currencyDisplay } = useCourierLocale();
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600 dark:text-gray-300">
        {name} {description && `(${formatDescription(description)})`}
      </span>
      <span className="dark:text-gray-200">{currencyDisplay}{parseFloat(amount).toFixed(2)}</span>
    </div>
  );
}
